import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

// ── List documents ────────────────────────────────────────────────────────────
// Filters: approvement_status, identity_id, from_date, to_date, page, per_page
export const fetchKYCDocuments = createAsyncThunk(
  "kycDocuments/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/kyc/documents", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load documents");
    }
  }
);

// ── Upload document ───────────────────────────────────────────────────────────
// formData must contain: doc_type, name, identity_id, frontside file,
//                        optionally: backside file, country
export const uploadKYCDocument = createAsyncThunk(
  "kycDocuments/upload",
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/didlogic/kyc/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(showToast({ message: res.data.message || "Document uploaded.", variant: "success" }));
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Upload failed";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ── Get single document ───────────────────────────────────────────────────────
export const fetchKYCDocument = createAsyncThunk(
  "kycDocuments/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/didlogic/kyc/documents/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load document");
    }
  }
);

// ── Update document ───────────────────────────────────────────────────────────
// payload: { id, name?, identity_id? }
export const updateKYCDocument = createAsyncThunk(
  "kycDocuments/update",
  async ({ id, ...payload }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/didlogic/kyc/documents/${id}`, payload);
      dispatch(showToast({ message: "Document updated.", variant: "success" }));
      return { id, updated: res.data?.data?.document || res.data?.data || null };
    } catch (err) {
      const msg = err.response?.data?.message || "Update failed";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ── Delete document ───────────────────────────────────────────────────────────
export const deleteKYCDocument = createAsyncThunk(
  "kycDocuments/delete",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/didlogic/kyc/documents/${id}`);
      dispatch(showToast({ message: "Document deleted.", variant: "success" }));
      return id;
    } catch (err) {
      const msg = err.response?.data?.message || "Delete failed";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ── List document countries ───────────────────────────────────────────────────
export const fetchDocumentCountries = createAsyncThunk(
  "kycDocuments/fetchCountries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/kyc/documents/countries", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load countries");
    }
  }
);

// ── List identities ───────────────────────────────────────────────────────────
export const fetchIdentities = createAsyncThunk(
  "kycDocuments/fetchIdentities",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/kyc/identities");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load identities");
    }
  }
);

// ── Create identity ───────────────────────────────────────────────────────────
/** Converts Rails-style { errors: { field: ["msg"] } } into a readable string */
function parseApiErrors(data) {
  if (!data) return "An unknown error occurred.";
  if (data.message) return data.message;
  if (data.error)   return data.error;
  // Rails validation errors: { errors: { name: ["has already been taken"] } }
  if (data.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .map(([field, msgs]) => {
        const label = field.charAt(0).toUpperCase() + field.slice(1);
        const msg   = Array.isArray(msgs) ? msgs[0] : msgs;
        return `${label} ${msg}`;
      })
      .join(". ");
  }
  return JSON.stringify(data);
}

export const createIdentity = createAsyncThunk(
  "kycDocuments/createIdentity",
  async (name, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/didlogic/kyc/identities", { name });
      dispatch(showToast({ message: "Identity created successfully.", variant: "success" }));
      return res.data;
    } catch (err) {
      const msg = parseApiErrors(err.response?.data);
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ── Archive identity ──────────────────────────────────────────────────────────
export const archiveIdentity = createAsyncThunk(
  "kycDocuments/archiveIdentity",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/didlogic/kyc/identities/${id}`);
      dispatch(showToast({ message: "Identity archived.", variant: "success" }));
      return id;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to archive identity";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const kycDocumentSlice = createSlice({
  name: "kycDocuments",
  initialState: {
    documents:      [],
    pagination:     null,
    loading:        false,

    currentDoc:        null,
    currentDocLoading: false,

    uploading: false,
    updating:  false,
    deleting:  false,

    countries:         [],
    countriesLoading:  false,

    identities:         [],
    identitiesLoading:  false,
    creatingIdentity:   false,
    archivingIdentity:  null,   // id currently being archived

    error: null,
  },
  reducers: {
    clearCurrentDoc(state) { state.currentDoc = null; },
  },
  extraReducers: (builder) => {
    builder
      // ── Fetch list ──────────────────────────────────────────────────────────
      .addCase(fetchKYCDocuments.pending,   (s) => { s.loading = true; s.error = null; })
      .addCase(fetchKYCDocuments.fulfilled, (s, a) => {
        s.loading = false;
        // DIDLogic response: { data: { documents: [], pagination: {} } }
        // OR our backend wraps it: { success: true, data: <didlogic response> }
        const inner = a.payload?.data;
        if (Array.isArray(inner)) {
          s.documents = inner;
        } else {
          s.documents  = inner?.documents || inner?.data || [];
          s.pagination = inner?.pagination || null;
        }
      })
      .addCase(fetchKYCDocuments.rejected,  (s, a) => { s.loading = false; s.error = a.payload; })

      // ── Upload ──────────────────────────────────────────────────────────────
      .addCase(uploadKYCDocument.pending,   (s) => { s.uploading = true; })
      .addCase(uploadKYCDocument.fulfilled, (s, a) => {
        s.uploading = false;
        // Response: { data: { document_id, document, message } }
        const doc = a.payload?.data?.document;
        if (doc) s.documents = [doc, ...s.documents];
      })
      .addCase(uploadKYCDocument.rejected,  (s) => { s.uploading = false; })

      // ── Fetch one ───────────────────────────────────────────────────────────
      .addCase(fetchKYCDocument.pending,    (s) => { s.currentDocLoading = true; })
      .addCase(fetchKYCDocument.fulfilled,  (s, a) => {
        s.currentDocLoading = false;
        s.currentDoc = a.payload?.data?.document || a.payload?.data || null;
      })
      .addCase(fetchKYCDocument.rejected,   (s) => { s.currentDocLoading = false; })

      // ── Update ──────────────────────────────────────────────────────────────
      .addCase(updateKYCDocument.pending,   (s) => { s.updating = true; })
      .addCase(updateKYCDocument.fulfilled, (s, a) => {
        s.updating = false;
        if (a.payload.updated) {
          const idx = s.documents.findIndex((d) => String(d.id) === String(a.payload.id));
          if (idx !== -1) s.documents[idx] = { ...s.documents[idx], ...a.payload.updated };
        }
      })
      .addCase(updateKYCDocument.rejected,  (s) => { s.updating = false; })

      // ── Delete ──────────────────────────────────────────────────────────────
      .addCase(deleteKYCDocument.pending,   (s) => { s.deleting = true; })
      .addCase(deleteKYCDocument.fulfilled, (s, a) => {
        s.deleting   = false;
        s.documents  = s.documents.filter((d) => String(d.id) !== String(a.payload));
      })
      .addCase(deleteKYCDocument.rejected,  (s) => { s.deleting = false; })

      // ── Countries ───────────────────────────────────────────────────────────
      .addCase(fetchDocumentCountries.pending,   (s) => { s.countriesLoading = true; })
      .addCase(fetchDocumentCountries.fulfilled, (s, a) => {
        s.countriesLoading = false;
        const payload = a.payload?.data;
        s.countries = Array.isArray(payload) ? payload : payload?.countries || [];
      })
      .addCase(fetchDocumentCountries.rejected,  (s) => { s.countriesLoading = false; })

      // ── Identities ──────────────────────────────────────────────────────────
      .addCase(fetchIdentities.pending,   (s) => { s.identitiesLoading = true; })
      .addCase(fetchIdentities.fulfilled, (s, a) => {
        s.identitiesLoading = false;
        const payload = a.payload?.data;
        s.identities = Array.isArray(payload)
          ? payload
          : payload?.identities || payload?.data || [];
      })
      .addCase(fetchIdentities.rejected,  (s) => { s.identitiesLoading = false; })

      // ── Create identity ──────────────────────────────────────────────────────
      .addCase(createIdentity.pending,   (s) => { s.creatingIdentity = true; })
      .addCase(createIdentity.fulfilled, (s, a) => {
        s.creatingIdentity = false;
        const identity = a.payload?.data?.identity || a.payload?.data;
        if (identity) s.identities = [identity, ...s.identities];
      })
      .addCase(createIdentity.rejected,  (s) => { s.creatingIdentity = false; })

      // ── Archive identity ─────────────────────────────────────────────────────
      .addCase(archiveIdentity.pending,   (s, a) => { s.archivingIdentity = a.meta.arg; })
      .addCase(archiveIdentity.fulfilled, (s, a) => {
        s.archivingIdentity = null;
        s.identities = s.identities.filter((i) => String(i.id) !== String(a.payload));
      })
      .addCase(archiveIdentity.rejected,  (s) => { s.archivingIdentity = null; });
  },
});

export const { clearCurrentDoc } = kycDocumentSlice.actions;
export default kycDocumentSlice.reducer;
